import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  CrudActionButton,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import apiService from 'library/api-service';
import { Grid2 as Grid } from '@mui/material';
import { renderDataGridLocaleText } from 'utils';
import { useSingleRowSelectionGuard } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import projectsActions from 'store/features/projects/actions';
import { CreateProjectDialog, UpdateProjectDialog } from './components';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { selectProjects, selectProjectsIsEmpty, selectProjectsLoading } from 'store/features/projects/selector';

const Projects = () => {
  // other hooks
  const intl = useIntl();
  const requireSingleRowSelection = useSingleRowSelectionGuard();

  // redux hooks
  const dispatch = useAppDispatch();
  const projects = useAppSelector(selectProjects);
  const loading = useAppSelector(selectProjectsLoading);
  const isEmptyProjects = useAppSelector(selectProjectsIsEmpty);

  // useState hooks
  const [openDeleteProject, setOpenDeleteProject] = useState(false);
  const [openCreateProject, setOpenCreateProject] = useState(false);
  const [openUpdateProject, setOpenUpdateProject] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'projects.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
      {
        field: 'groupTitle',
        headerName: intl.formatMessage({ id: 'projects.groupTitle', defaultMessage: 'Grup Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const refresh = async () => {
    dispatch(projectsActions.getProjects());
  };

  // sync helper functions
  const handleCloseDeleteProject = () => setOpenDeleteProject(false);
  const handleClickOpenDeleteProject = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;
    setOpenDeleteProject(true);
  };

  const handleCloseCreateProject = () => setOpenCreateProject(false);
  const handleClickOpenCreateProject = () => setOpenCreateProject(true);

  const handleCloseUpdateProject = () => setOpenUpdateProject(false);
  const handleClickOpenUpdateProject = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;
    setOpenUpdateProject(true);
  };

  // useEffect hooks
  useEffect(() => {
    if (!projects.length && !isEmptyProjects) {
      dispatch(projectsActions.getProjects());
    }
  }, [projects, isEmptyProjects, dispatch]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['create-product']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="create" onClick={handleClickOpenCreateProject}>
              <FormattedMessage id="projects.createProject" defaultMessage="Ürün Oluştur" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-product']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="update" onClick={handleClickOpenUpdateProject}>
              <FormattedMessage id="projects.updateProject" defaultMessage="Ürün Düzenle" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-product']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="delete" onClick={handleClickOpenDeleteProject}>
              <FormattedMessage id="projects.deleteProject" defaultMessage="Ürün Sil" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateProjectDialog open={openCreateProject} handleClose={handleCloseCreateProject} refresh={refresh} />
      <UpdateProjectDialog
        open={openUpdateProject}
        projectId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateProject}
        refresh={refresh}
      />
      <DeleteDialog
        open={openDeleteProject}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteProject}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="projects.deleteProject" defaultMessage="Ürün Sil" />}
        subTitle={
          <FormattedMessage
            id="gibTypes.deleteGibTypeRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra GİB Yöntemi veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteProject}
        successMessage={
          <FormattedMessage
            id="projects.deleteProjectRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra ürün veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          rows={projects}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default Projects;
